let audioCtx: AudioContext | null = null
let muted: boolean | null = null

const MUTE_STORAGE_KEY = "hl-muted"

export function isSoundMuted(): boolean {
  if (muted === null) {
    if (typeof window === "undefined") return true
    try {
      muted = window.localStorage.getItem(MUTE_STORAGE_KEY) === "1"
    } catch {
      muted = false
    }
  }
  return muted
}

export function setSoundMuted(value: boolean) {
  muted = value
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, value ? "1" : "0")
  } catch {
    // Preference just won't persist
  }
}

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined" || isSoundMuted()) return null
  if (!audioCtx) {
    try {
      audioCtx = new AudioContext()
    } catch {
      return null
    }
  }
  // Resume if suspended (browser autoplay policy)
  if (audioCtx.state === "suspended") {
    audioCtx.resume()
  }
  return audioCtx
}

// Browsers keep the AudioContext suspended until a user gesture. Warm it on
// the first interaction so timer ticks can play before the first guess.
if (typeof window !== "undefined") {
  window.addEventListener(
    "pointerdown",
    () => {
      if (!isSoundMuted()) getAudioContext()
    },
    { once: true }
  )
}

/** Ticking bomb countdown for final 3 seconds with a low resonant knock */
export function playUrgentTickSound() {
  const ctx = getAudioContext()
  if (!ctx) return

  const t = ctx.currentTime

  // Low percussive knock like a clock or bomb tick
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)

  osc.frequency.setValueAtTime(120, t)
  osc.frequency.exponentialRampToValueAtTime(60, t + 0.12)
  osc.type = "sine"

  gain.gain.setValueAtTime(0.15, t)
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15)

  osc.start(t)
  osc.stop(t + 0.15)

  // Click transient on top
  const noise = ctx.createOscillator()
  const noiseGain = ctx.createGain()
  noise.connect(noiseGain)
  noiseGain.connect(ctx.destination)

  noise.frequency.value = 800
  noise.type = "triangle"

  noiseGain.gain.setValueAtTime(0.08, t)
  noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.03)

  noise.start(t)
  noise.stop(t + 0.03)
}

/** Subtle tap when selecting higher/lower */
export function playSelectSound() {
  const ctx = getAudioContext()
  if (!ctx) return

  const t = ctx.currentTime

  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)

  osc.frequency.value = 440
  osc.type = "sine"

  gain.gain.setValueAtTime(0.04, t)
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.06)

  osc.start(t)
  osc.stop(t + 0.06)
}

/** Correct answer - bright ascending double chime */
export function playCorrectSound() {
  const ctx = getAudioContext()
  if (!ctx) return

  const t = ctx.currentTime

  // First note (C5)
  const osc1 = ctx.createOscillator()
  const gain1 = ctx.createGain()
  osc1.connect(gain1)
  gain1.connect(ctx.destination)
  osc1.frequency.value = 523
  osc1.type = "sine"
  gain1.gain.setValueAtTime(0.1, t)
  gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.2)
  osc1.start(t)
  osc1.stop(t + 0.2)

  // Second note (E5) - slightly delayed
  const osc2 = ctx.createOscillator()
  const gain2 = ctx.createGain()
  osc2.connect(gain2)
  gain2.connect(ctx.destination)
  osc2.frequency.value = 659
  osc2.type = "sine"
  gain2.gain.setValueAtTime(0, t + 0.08)
  gain2.gain.linearRampToValueAtTime(0.12, t + 0.1)
  gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.35)
  osc2.start(t + 0.08)
  osc2.stop(t + 0.35)
}

/** Wrong answer - soft descending tone */
export function playWrongSound() {
  const ctx = getAudioContext()
  if (!ctx) return

  const t = ctx.currentTime

  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)

  osc.frequency.setValueAtTime(400, t)
  osc.frequency.exponentialRampToValueAtTime(250, t + 0.3)
  osc.type = "sine"

  gain.gain.setValueAtTime(0.1, t)
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4)

  osc.start(t)
  osc.stop(t + 0.4)
}

/** Victory - bright ascending arpeggio for finishing a full run */
export function playVictorySound() {
  const ctx = getAudioContext()
  if (!ctx) return

  const t = ctx.currentTime
  const notes = [523, 659, 784, 1047] // C5, E5, G5, C6

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)

    const start = t + i * 0.12
    osc.frequency.value = freq
    osc.type = "sine"

    gain.gain.setValueAtTime(0, start)
    gain.gain.linearRampToValueAtTime(0.1, start + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.4)

    osc.start(start)
    osc.stop(start + 0.4)
  })
}

/** Game over - descending three-note sequence */
export function playGameOverSound() {
  const ctx = getAudioContext()
  if (!ctx) return

  const t = ctx.currentTime
  const notes = [392, 330, 262] // G4, E4, C4

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)

    const start = t + i * 0.15
    osc.frequency.value = freq
    osc.type = "sine"

    gain.gain.setValueAtTime(0, start)
    gain.gain.linearRampToValueAtTime(0.08, start + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3)

    osc.start(start)
    osc.stop(start + 0.3)
  })
}
